import {
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react"
import styles from "./Tabs.module.css"

export interface TabItem {
  id: string
  label: string
  panel: ReactNode
}

// The keys that move between tabs, and where each goes from `index` of `count`.
const MOVES: Record<string, (index: number, count: number) => number> = {
  ArrowRight: (index, count) => (index + 1) % count,
  ArrowLeft: (index, count) => (index - 1 + count) % count,
  Home: () => 0,
  End: (_, count) => count - 1,
}

/**
 * Tabs over panels, as the WAI-ARIA tabs pattern has them: the arrow keys move
 * between tabs and choose as they go, Home and End reach either end, and only
 * the chosen tab is in the page's Tab order. Every panel stays in the page,
 * hidden unless chosen, so each tab's `aria-controls` names something real.
 */
export function Tabs({
  label,
  tabs,
  defaultTabId,
}: {
  label: string
  tabs: TabItem[]
  /** The tab that opens chosen; the first when none is given. */
  defaultTabId?: string
}) {
  const [chosenId, setChosenId] = useState(defaultTabId ?? tabs[0]?.id)
  const base = useId()
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

  function onKeyDown(event: KeyboardEvent) {
    const move = MOVES[event.key]
    if (!move) return
    event.preventDefault()
    const index = tabs.findIndex((tab) => tab.id === chosenId)
    const next = move(index, tabs.length)
    setChosenId(tabs[next]!.id)
    tabRefs.current[next]?.focus()
  }

  return (
    <>
      <div role="tablist" aria-label={label} className={styles.list}>
        {tabs.map((tab, index) => {
          const isChosen = tab.id === chosenId
          return (
            <button
              key={tab.id}
              ref={(element) => {
                tabRefs.current[index] = element
              }}
              type="button"
              role="tab"
              id={`${base}-tab-${tab.id}`}
              aria-selected={isChosen}
              aria-controls={`${base}-panel-${tab.id}`}
              tabIndex={isChosen ? 0 : -1}
              className={styles.tab}
              onClick={() => setChosenId(tab.id)}
              onKeyDown={onKeyDown}
            >
              {tab.label}
            </button>
          )
        })}
      </div>
      {tabs.map((tab) => (
        <div
          key={tab.id}
          role="tabpanel"
          id={`${base}-panel-${tab.id}`}
          aria-labelledby={`${base}-tab-${tab.id}`}
          hidden={tab.id !== chosenId}
          className={styles.panel}
        >
          {tab.panel}
        </div>
      ))}
    </>
  )
}
